import React, { Component, createRef } from 'react';

import Spinner from '@atlaskit/spinner';

import { FEATURE_FLAG } from '@qality/common-config';
import { messages } from '@qality/common-messages';
import { AttachmentFile, TestStep, TestStepsView } from '@qality/common-model';
import {
  StorageOfUniqueServices,
  TestStepService,
} from '@qality/common-service';
import { PasteAttachmentsUtils } from '@qality/common-utils';

import TestCaseBoxNewAttachments from './TestCaseBoxNewAttachments';
import { NewStepContainerStyled } from './TestCaseBoxStep.styled';
import TestStepActions from './TestStepActions';
import TestStepInput from './TestStepInput';
import './_test-case-box-step.scss';

type Props = {
  canEdit: boolean;
  testStep: TestStep;
  view: TestStepsView;
  testStepIndex: number;
  deleteTestStep: (testStepIndex: number, testStep?: TestStep) => void;
  onUpdateTestStep: (
    testStepIndex: number,
    testStep: TestStep,
    isNewTestStep?: boolean
  ) => Promise<void>;
  testCaseIssueId?: number;
  canCreateAttachment: boolean;
  isFullscreenMode?: boolean;
};

type State = {
  step: string;
  result: string;
  data: string;
  loading: boolean;
  files: Array<AttachmentFile>;
  hideStep: boolean;
  pastedAttachments: Array<File>;
};

export default class TestCaseBoxNewStep extends Component<Props, State> {
  newStepRef = createRef<HTMLDivElement>();
  constructor(props: Props) {
    super(props);
    this.state = {
      step: '',
      result: '',
      data: '',
      loading: false,
      files: [],
      hideStep: false,
      pastedAttachments: null,
    };
  }

  onUpdateStepField = (): void => {
    const { step, result, data, files } = this.state;
    const testCaseId = this.props.testCaseIssueId;
    const updatedTestStep = {
      ...this.props.testStep,
      testCaseId,
      step,
      result,
      data,
      attachmentsTemp: files,
    };
    this.props.onUpdateTestStep(
      this.props.testStepIndex,
      updatedTestStep,
      false
    );
  };

  onStepChange = async (newStep: string): Promise<void> => {
    await this.setState({ step: newStep });
    this.onUpdateStepField();
  };

  onTestDataChange = async (data: string): Promise<void> => {
    await this.setState({ data });
    this.onUpdateStepField();
  };

  onTestResultChange = async (result: string): Promise<void> => {
    await this.setState({ result });
    this.onUpdateStepField();
  };

  setAttachmentFiles = async (files: Array<AttachmentFile>): Promise<void> => {
    await this.setState({ files: files });
    this.onUpdateStepField();
  };

  handleOnPaste = (e: React.ClipboardEvent<HTMLDivElement>): void => {
    const isPasteEnabled = [
      FEATURE_FLAG.isPremium,
      this.props.canEdit,
      this.props.canCreateAttachment,
      this.props.view.isDetailView,
    ].every(Boolean);

    if (isPasteEnabled) {
      this.setState({
        pastedAttachments:
          PasteAttachmentsUtils.convertPastedFiledToAttachments(e),
      });
    }
  };

  clearPastedAttachments = (): void => {
    this.setState({
      pastedAttachments: null,
    });
  };

  saveTestStep = async (): Promise<void> => {
    try {
      this.setState({ loading: true });
      if (this.props.testCaseIssueId) {
        const { step, result, data } = this.state;
        const attachments = await StorageOfUniqueServices.get(
          'AttachmentService'
        ).postAttachments(this.props.testCaseIssueId, this.state.files);
        const testCaseId = this.props.testCaseIssueId;
        const updatedTestStep: TestStep = {
          ...this.props.testStep,
          testCaseId,
          step,
          result,
          data,
          isNew: false,
          attachments,
        };
        updatedTestStep.origin =
          TestStepService.createTestStep(updatedTestStep);
        this.props.onUpdateTestStep(
          this.props.testStepIndex,
          updatedTestStep,
          true
        );
      }
    } catch (e) {
      StorageOfUniqueServices.get('ErrorHandlingUtils').handleError(
        e,
        messages['error.testCaseBox.saveTestStep.message']
      );
    }
  };

  getColumnsWidths = (): {
    testStepColumnWidth: string;
    testDataColumnWidth: string;
    expectedResultColumnWidth: string;
    attachmentColumnWidth: string;
  } => {
    // Note: Values 27% and 19% are the same as in TestCaseBox.
    // 15px equals to 55px of TestStepActions in a new step row
    // minus 40px of TestStepMenu in a normal step row.
    // Substract 1px to compensate borders.
    // Deliberately leaving formulas, instead of using computed values.
    const widthDifferencePx = '15px';
    const borderWidthPx = '1px';

    if (this.props.view.isDetailView) {
      const width = `calc(27% + ${widthDifferencePx} / 3 - ${borderWidthPx})`;

      return {
        testStepColumnWidth: width,
        testDataColumnWidth: width,
        expectedResultColumnWidth: width,
        attachmentColumnWidth: `calc(19% - ${widthDifferencePx})`,
      };
    }

    return {
      testStepColumnWidth: `calc(50% + ${widthDifferencePx} / 2)`,
      testDataColumnWidth: `0px`,
      expectedResultColumnWidth: `calc(50% - ${widthDifferencePx} / 2)`,
      attachmentColumnWidth: `0px`,
    };
  };

  isStepEmpty = (): boolean => {
    return (
      this.state.step === '' &&
      this.state.result === '' &&
      this.state.data === '' &&
      this.state.files.length === 0
    );
  };

  // Firefox for some reason has multiple focuses.
  // When editor tries to add selection/carret to where user clicked it's bugged,
  // probably because of multiple focuses.
  // To avoid this blur hide editor before moving to the next.
  // eslint-disable-next-line complexity
  handleKeyDown = (e): void => {
    if (e.key === 'Tab' && e.shiftKey && this.isStepEmpty()) {
      this.setState({
        hideStep: true,
      });
      // NOTE: queueMicrotask is required here. Otherwise it will not work
      queueMicrotask(() => {
        this.props.deleteTestStep(this.props.testStepIndex);
      });
    }
  };

  componentDidMount = (): void => {
    if (this.props.isFullscreenMode) {
      this.newStepRef.current.scrollIntoView();
    }
  };

  // eslint-disable-next-line complexity
  render(): JSX.Element {
    const { isDetailView } = this.props.view;
    const {
      testStepColumnWidth,
      testDataColumnWidth,
      expectedResultColumnWidth,
      attachmentColumnWidth,
    } = this.getColumnsWidths();

    if (this.state.hideStep) {
      return null;
    }

    return (
      <NewStepContainerStyled onPaste={this.handleOnPaste}>
        <div
          className='test-step-inputs hide-focus-wrapper'
          ref={this.newStepRef}
        >
          <div
            className='column-row'
            style={{ width: testStepColumnWidth }}
            onKeyDown={this.handleKeyDown}
          >
            <TestStepInput onChange={this.onStepChange} autofocus={true} />
          </div>
          {isDetailView && (
            <div className='column-row' style={{ width: testDataColumnWidth }}>
              <TestStepInput
                onChange={this.onTestDataChange}
                autofocus={false}
              />
            </div>
          )}
          <div
            className='column-row'
            style={{ width: expectedResultColumnWidth }}
          >
            <TestStepInput
              onChange={this.onTestResultChange}
              autofocus={false}
            />
          </div>
          {isDetailView && (
            <div
              className='column-row'
              style={{ width: attachmentColumnWidth }}
            >
              <TestCaseBoxNewAttachments
                onAttachmentFilesChanged={this.setAttachmentFiles}
                files={this.state.files}
                canEdit={this.props.canEdit}
                canCreateAttachment={this.props.canCreateAttachment}
                pastedAttachments={this.state.pastedAttachments}
                clearPastedAttachments={this.clearPastedAttachments}
              />
            </div>
          )}
        </div>
        <div className='test-case-box-new-step-actions'>
          <TestStepActions
            testStepIndex={this.props.testStepIndex}
            saveTestStep={this.saveTestStep}
            deleteTestStep={this.props.deleteTestStep}
          />
        </div>
        {this.state.loading && (
          <div className='drop-container-spinner'>
            <Spinner size='medium' />
          </div>
        )}
      </NewStepContainerStyled>
    );
  }
}
